import React from 'react';
import { Link } from 'react-router-dom';
import {
  Repeat, Target, Cog, AlertTriangle, Users, Dumbbell, History, FileDown, Building2, ShieldCheck
} from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const features = [
  { icon: Repeat, title: 'Recurring pattern tracking', text: 'Track issues that repeat across a team\'s match history, not just a single game.' },
  { icon: Target, title: 'Post-match verdict', text: 'A clear conclusion on what decided the match, not another dashboard to interpret.' },
  { icon: Cog, title: 'Computed Analysis Engine', text: 'Tactmark runs deterministic scoring algorithms, tactical weighting, failure-mode classification, and risk modeling before any report prose is written.' },
  { icon: AlertTriangle, title: 'Tactmark Match Risk Model', text: 'Score risk moments across match damage, repeatability, opponent exploitability, evidence confidence, training urgency, and recovery difficulty.' },
  { icon: AlertTriangle, title: 'Decisive moment review', text: 'Break down goals, chances, transitions, set pieces, and coach-flagged moments.' },
  { icon: AlertTriangle, title: 'Mistake attribution', text: 'Connect each decisive moment to the tactical, technical, or decision mistake behind it.' },
  { icon: Users, title: 'Coach Decision Review', text: 'Help staff review whether selection, role, fitness, or substitution decisions matched the game state.' },
  { icon: Dumbbell, title: 'Training recommendations', text: 'Turn match evidence into coaching priorities and training recommendations for the next session.' },
  { icon: History, title: 'Team report history', text: 'Keep every match report organized by team, season, and opponent.' },
  { icon: FileDown, title: 'Shareable coaching reports', text: 'Share structured match reports with coaching and performance departments. Export as PDF for offline distribution and staff review.' },
  { icon: Building2, title: 'Multi-team organization workspace', text: 'Support clubs, academies, and federations managing multiple teams.' },
  { icon: ShieldCheck, title: 'Private organization workspaces', text: 'Keep teams, matches, reports, and staff access organized within your organization.' },
];

export default function Features() {
  usePageMeta(
    'Features | Coaching Decisions After the Match',
    'Tactmark is football post-match analysis software with structured coaching reports, training recommendations, decisive moment review, recurring pattern tracking, and shareable coaching reports for clubs, academies, national teams, and federations.'
  );
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <div className="max-w-3xl mx-auto px-6 pt-16 pb-4 text-center">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Features</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy-900 leading-tight mb-6">
          Football post-match analysis software built for coaching and performance departments.
        </h1>
        <p className="text-gray-600 text-lg leading-relaxed">
          Every feature supports one workflow: turning match evidence and coaching observations into structured post-match analysis reports, coaching priorities, and evidence-based training decisions.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {features.map(({ icon: Icon, title, text }) => (
            <div key={title}>
              <div className="w-10 h-10 rounded-lg bg-navy-50 border border-navy-100 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-navy-700" />
              </div>
              <h3 className="text-navy-900 font-semibold mb-1.5">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl text-navy-900 mb-4">Ready to try it on your own match?</h2>
        <Link
          to="/login?mode=signup"
          className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-8 py-3 rounded transition-colors mt-2"
        >
          Start free trial
        </Link>
      </div>

      <PublicFooter />
    </div>
  );
}
